import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, num, fmtDate } from "../lib/api";
import { PageHeader, Card, StatusBadge, Button } from "../components/shared";
import { ArrowLeft, Check, ArrowRight, Package } from "lucide-react";

const STAGE_COLORS = {
  Cutting: "#0284C7", Embroidery: "#8B5CF6", Printing: "#EC4899", Emboss: "#D946EF",
  Stitching: "#F59E0B", Bartec: "#10B981", Washing: "#06B6D4", "Thread Cutting": "#84CC16",
  Checking: "#EAB308", Finishing: "#6366F1",
};

export default function ProductionOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  const load = () => api.get(`/production-orders/${id}`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!d) return <div className="h-96 grid place-items-center"><div className="h-10 w-10 border-4 border-blue-800 border-t-transparent rounded-full animate-spin" /></div>;

  const stageAvail = (s) => (s.qty_in || 0) - (s.qty_issued || 0);
  const balance = d.total_qty - (d.finished_qty || 0);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="secondary" size="sm" onClick={() => navigate("/production-orders")}><ArrowLeft size={15} /> Back</Button>
      </div>
      <PageHeader title={`${d.code} · ${d.design_number}`} subtitle={`${d.item_type} · ${d.colour} · ${d.gender}`}>
        <StatusBadge status={d.status} />
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {[["Order Qty", num(d.total_qty)], ["Finished", num(d.finished_qty)], ["Balance", num(balance)],
          ["Current Process", d.current_process], ["Fabric Lots", (d.fabric_lot_numbers || []).join(", ") || "-"], ["Target", fmtDate(d.target_date)]].map(([l, v]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
            <p className="text-xs text-slate-500 uppercase tracking-wide">{l}</p>
            <p className="text-sm font-bold text-slate-800 mt-1 font-head">{v}</p>
          </div>
        ))}
      </div>

      <Card className="p-5 mb-6">
        <p className="text-sm font-semibold text-slate-800 mb-3 font-head">Order Details</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          {[["Design Number", d.design_number], ["Item Type", d.item_type], ["Gender", d.gender || "-"],
            ["Colour", d.colour], ["Sales / Customer Ref", d.sales_ref || "-"], ["Cutting Master", d.cutting_master || "-"],
            ["Cutting Ratio", d.cutting_ratio || "-"], ["Start Date", fmtDate(d.start_date)],
            ["Target Date", fmtDate(d.target_date)],
            ["Cutting Sheet No", (d.cuttings || []).map((c) => c.cutting_sheet_number).filter(Boolean).join(", ") || "-"],
            ["Consumption", (d.cuttings || []).map((c) => c.consumption).filter(Boolean).join(", ") || "-"],
            ["Selected Processes", (d.selected_optional || []).join(", ") || "None"]].map(([l, v]) => (
            <div key={l}>
              <p className="text-xs text-slate-500">{l}</p>
              <p className="font-medium text-slate-800 mt-0.5">{v}</p>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <p className="text-xs text-slate-500 mb-1">Size-wise Order Quantity</p>
          <div className="flex flex-wrap gap-2">
            {(d.sizes || []).map((s, i) => (
              <span key={i} className="px-2.5 py-1 rounded-md bg-slate-50 border border-slate-200 text-xs">
                <b>{s.size}</b> : {num(s.qty)}
              </span>
            ))}
          </div>
        </div>
        {d.remarks && <p className="text-sm text-slate-600 mt-3"><span className="font-semibold">Remarks:</span> {d.remarks}</p>}
      </Card>

      <Card className="p-5 mb-6">
        <p className="text-sm font-semibold text-slate-800 mb-4 font-head">Process Timeline</p>
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          <Node label="Fabric" done color="#3B82F6" value={(d.fabric_lot_numbers || []).length ? "Issued" : "—"} icon={Package} />
          {(d.stages || []).map((s) => {
            const done = s.status === "Completed" || s.status === "Received" || stageAvail(s) > 0;
            return (
              <React.Fragment key={s.name}>
                <ArrowRight size={16} className="text-slate-300 shrink-0" />
                <Node label={s.name} color={STAGE_COLORS[s.name]} done={done}
                  value={`In ${num(s.qty_in)}${s.qty_pending ? ` · ${num(s.qty_pending)} out` : ""}`}
                  short={s.short} status={s.status} />
              </React.Fragment>
            );
          })}
          <ArrowRight size={16} className="text-slate-300 shrink-0" />
          <Node label="Finished Goods" color="#22C55E" done={d.finished_qty > 0} value={`${num(d.finished_qty)} pcs`} icon={Package} />
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <TraceCard title="Fabric & Cutting">
          {d.cuttings?.length ? d.cuttings.map((c) => (
            <Row key={c.id} a={`Cut ${num(c.total_qty)} pcs`} b={`Lot ${c.lot_number} · ${num(c.fabric_issued_qty)} mtr`} c={fmtDate(c.cutting_date)} />
          )) : <Empty />}
        </TraceCard>
        <TraceCard title="Process Issues & Challans">
          {d.issues?.length ? d.issues.map((i) => (
            <Row key={i.id} a={`${i.from_process} → ${i.to_process} · ${num(i.total_qty)} pcs`}
              b={`${i.jobber_name || "In-House"}${i.challan_number ? ` · ${i.challan_number}` : ""}`}
              c={<StatusBadge status={i.status} />} onClick={i.challan_number ? () => navigate(`/challans/${i.challan_number}`) : null} />
          )) : <Empty />}
        </TraceCard>
        <TraceCard title="Receipts & Shortages">
          {d.receipts?.length ? d.receipts.map((r) => (
            <Row key={r.id} a={`${r.process} · Rec ${num(r.received_qty)}`}
              b={`${r.jobber_name || "-"}${r.short_qty > 0 ? ` · Short ${num(r.short_qty)}` : ""}`}
              c={fmtDate(r.actual_receiving_date)} danger={r.short_qty > 0} />
          )) : <Empty />}
        </TraceCard>
        <TraceCard title="Accessories & Finished Goods">
          {d.accessory_issues?.map((a) => (
            <Row key={a.id} a={`${a.item_name} · ${num(a.quantity)} ${a.unit || ""}`} b={a.jobber_name || "In-House"} c={fmtDate(a.date)} />
          ))}
          {d.finished_goods?.map((f) => (
            <Row key={f.id} a={`Finished ${num(f.quantity)} pcs`} b={f.location} c={fmtDate(f.date)} />
          ))}
          {!d.accessory_issues?.length && !d.finished_goods?.length && <Empty />}
        </TraceCard>
      </div>
    </div>
  );
}

function Node({ label, value, color, done, short, status, icon: Icon }) {
  return (
    <div className="shrink-0 w-28 text-center">
      <div className="mx-auto h-11 w-11 rounded-full grid place-items-center text-white relative"
        style={{ background: done ? color : "#e2e8f0" }}>
        {Icon ? <Icon size={18} /> : done ? <Check size={18} /> : <span className="text-xs font-bold" style={{ color: "#94a3b8" }}>•</span>}
        {short > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] grid place-items-center">!</span>}
      </div>
      <p className="text-xs font-semibold text-slate-700 mt-1.5 leading-tight">{label}</p>
      <p className="text-[10px] text-slate-400 leading-tight">{value}</p>
      {status && <p className="text-[9px] text-slate-400">{status}</p>}
    </div>
  );
}

function TraceCard({ title, children }) {
  return (
    <Card className="p-4">
      <p className="text-sm font-semibold text-slate-800 mb-2 font-head">{title}</p>
      <div className="divide-y divide-slate-100">{children}</div>
    </Card>
  );
}
function Row({ a, b, c, danger, onClick }) {
  return (
    <div onClick={() => onClick && onClick()} className={`flex items-center justify-between py-2 gap-2 ${onClick ? "cursor-pointer hover:bg-slate-50 -mx-1 px-1 rounded" : ""}`}>
      <div className="min-w-0">
        <p className={`text-sm font-medium ${danger ? "text-red-700" : "text-slate-700"} truncate`}>{a}</p>
        <p className="text-xs text-slate-400 truncate">{b}</p>
      </div>
      <span className="text-xs text-slate-500 shrink-0">{c}</span>
    </div>
  );
}
function Empty() { return <p className="text-sm text-slate-400 py-3">No records</p>; }
